'use client';

import { Box, Container, Skeleton, Stack, Divider } from '@mui/material';

export default function PageSkeleton() {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'white', px: 2, py: 8 }}>
      <Container maxWidth="lg">
        
        {/* ── Encabezado ── */}
        <Box sx={{ mb: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <Skeleton variant="rounded" width={288} height={40} sx={{ bgcolor: '#dbeafe' }} />
          <Skeleton variant="text" width={320} height={20} sx={{ bgcolor: '#f3f4f6' }} />
          <Skeleton variant="text" width={96} height={20} sx={{ bgcolor: '#f3f4f6' }} />
        </Box>

        {/* ── Leyenda ── */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center', gap: 5, flexWrap: 'wrap' }}>
          {[80, 64, 96, 64].map((w, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Skeleton variant="circular" width={12} height={12} sx={{ bgcolor: '#e5e7eb' }} />
              <Skeleton variant="text" width={w} height={20} sx={{ bgcolor: '#f3f4f6' }} />
            </Box>
          ))}
        </Box>

        {/* ── SVG + Estado (Simulando Grid con Box) ── */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(12, 1fr)' }, 
          gap: 3, 
          alignItems: 'start' 
        }}>
          {/* Simulación SVG (Col-span 9) */}
          <Box sx={{ gridColumn: { md: 'span 8', lg: 'span 9' } }}>
            <Box sx={{ 
              borderRadius: '16px', 
              border: '1px solid #bfdbfe', 
              bgcolor: '#f0f9ff', 
              p: 1.5 
            }}>
              <Skeleton 
                variant="rectangular" 
                width="100%" 
                sx={{ 
                  aspectRatio: '780/420', 
                  borderRadius: '12px', 
                  bgcolor: 'rgba(219, 234, 254, 0.6)' 
                }} 
              />
            </Box>
          </Box>
          
          {/* Simulación Panel Estado (Col-span 3) */}
          <Box sx={{ gridColumn: { md: 'span 4', lg: 'span 3' } }}>
            <Box sx={{ 
              borderRadius: '16px', 
              border: '1px solid #bfdbfe', 
              bgcolor: '#f9fafb', 
              p: 2 
            }}>
              <Box sx={{ mb: 2, borderBottom: '1px solid #e5e7eb', pb: 1.5 }}>
                <Skeleton variant="text" width="70%" height={24} sx={{ bgcolor: '#dbeafe', mb: 1 }} />
                <Skeleton variant="text" width="50%" height={16} sx={{ bgcolor: '#f3f4f6' }} />
              </Box>
              <Stack spacing={2}>
                <Skeleton variant="rounded" height={80} sx={{ borderRadius: '12px', bgcolor: '#eff6ff', border: '1px solid #dbeafe' }} />
                <Skeleton variant="rounded" height={56} sx={{ borderRadius: '12px', bgcolor: '#eff6ff', border: '1px solid #dbeafe' }} />
              </Stack>
            </Box>
          </Box>
        </Box>

        {/* ── Niveles (3 columnas) ── */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, 
          gap: 2, 
          mt: 3 
        }}>
          {[0, 1, 2].map((i) => (
            <Box key={i} sx={{ 
              p: 2, 
              borderRadius: '12px', 
              border: '1px solid #bfdbfe', 
              bgcolor: 'white',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}>
              <Skeleton variant="text" width={80} height={16} sx={{ bgcolor: '#f3f4f6', mb: 1.5 }} />
              <Skeleton variant="rectangular" height={6} sx={{ borderRadius: 5, bgcolor: '#f3f4f6', mb: 1 }} />
              <Skeleton variant="text" width={32} height={16} sx={{ ml: 'auto', bgcolor: '#f3f4f6' }} />
            </Box>
          ))}
        </Box>

        {/* ── Tarjetas Acción (3 columnas) ── */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, 
          gap: 2, 
          mt: 4 
        }}>
          {[0, 1, 2].map((i) => (
            <Box key={i} sx={{ 
              p: 2, 
              borderRadius: '16px', 
              border: '1px solid #bfdbfe', 
              bgcolor: '#eff6ff',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5
            }}>
              <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: '8px', bgcolor: '#dbeafe' }} />
              <Skeleton variant="text" width={100} height={20} sx={{ bgcolor: '#dbeafe' }} />
              <Skeleton variant="text" width={80} height={16} sx={{ bgcolor: '#f3f4f6' }} />
              <Skeleton variant="rectangular" width="100%" height={28} sx={{ borderRadius: '8px', bgcolor: '#bfdbfe', mt: 1 }} />
            </Box>
          ))}
        </Box>

        <Divider sx={{ my: 8 }} />

        {/* ── Sección de Información (Stack normal) ── */}
        <Box sx={{ mb: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <Skeleton variant="rounded" width={192} height={32} sx={{ bgcolor: '#dbeafe' }} />
          <Skeleton variant="text" width={224} height={20} sx={{ bgcolor: '#f3f4f6' }} />
        </Box>

        <Stack spacing={2}>
          {[0, 1, 2].map((i) => (
            <Box key={i} sx={{ 
              display: 'flex', 
              gap: 3, 
              p: 3, 
              borderRadius: '16px', 
              border: '1px solid #bfdbfe', 
              borderLeft: '4px solid #60a5fa', 
              bgcolor: '#eff6ff' 
            }}>
              <Skeleton variant="rounded" width={48} height={48} sx={{ borderRadius: '12px', bgcolor: '#dbeafe', flexShrink: 0 }} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width={160} height={24} sx={{ bgcolor: '#dbeafe', mb: 1 }} />
                <Skeleton variant="text" width="100%" height={16} sx={{ bgcolor: '#f3f4f6' }} />
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Skeleton variant="rounded" width={80} height={20} sx={{ borderRadius: 10, bgcolor: '#dbeafe' }} />
                  <Skeleton variant="rounded" width={100} height={20} sx={{ borderRadius: 10, bgcolor: '#dbeafe' }} />
                </Box>
              </Box>
            </Box>
          ))}
        </Stack>

      </Container>
    </Box>
  );
}